import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { AudioEngine } from "../audio/AudioEngine";
import { createToneAdapter } from "../audio/tone";
import { ControlStore } from "../state/controlStore";
import { createDefaultControls, seedFromLocation } from "../state/defaultControls";
import { ErrorLog } from "../state/errorLog";

export interface EngineContextValue {
  store: ControlStore;
  errorLog: ErrorLog;
  /** Creates the engine on first use, and replaces terminal instances. */
  ensureEngine: () => AudioEngine;
  /** Null until the user starts audio. */
  getEngine: () => AudioEngine | null;
}

const EngineContext = createContext<EngineContextValue | null>(null);

/**
 * Owner of the AudioEngine instance.
 *
 * This provider is mounted OUTSIDE <StrictMode> (see main.tsx), so its effect
 * setup/cleanup runs once. That removes any need to distinguish StrictMode's
 * simulated cleanup from a real unmount — no timer heuristics.
 *
 * The engine itself is created lazily on user action, so a double-invoked effect
 * could never create or tear down an AudioContext anyway.
 */
export function EngineProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<ControlStore | null>(null);
  const errorLogRef = useRef<ErrorLog | null>(null);
  const engineRef = useRef<AudioEngine | null>(null);

  storeRef.current ??= new ControlStore(
    createDefaultControls(seedFromLocation(window.location.search)),
  );
  errorLogRef.current ??= new ErrorLog();

  const store = storeRef.current;
  const errorLog = errorLogRef.current;

  const ensureEngine = (): AudioEngine => {
    const current = engineRef.current;
    if (current === null || current.status === "disposed" || current.status === "failed") {
      // Terminal instances are never reused: a failed init has rolled back its
      // nodes and callbacks, and reusing it would double-register them.
      engineRef.current = new AudioEngine({
        tone: createToneAdapter(),
        store,
        errorLog,
      });
    }
    // Non-null: just assigned above when it was null.
    return engineRef.current as AudioEngine;
  };

  useEffect(() => {
    function onPageHide(event: PageTransitionEvent) {
      if (event.persisted) {
        // Entering the back/forward cache: keep nodes and callbacks alive.
        engineRef.current?.pause();
      } else {
        engineRef.current?.dispose();
      }
    }

    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) {
        // Returning from BFCache: the engine is already paused (see onPageHide);
        // only the AudioContext may need re-acquiring.
        engineRef.current?.recoverContext();
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        // Becoming visible must never stop playback — only recover the context.
        engineRef.current?.recoverContext();
      }
    }

    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      // Reached only on a real unmount, because this provider is outside StrictMode.
      engineRef.current?.dispose();
    };
  }, []);

  return (
    <EngineContext.Provider
      value={{
        store,
        errorLog,
        ensureEngine,
        getEngine: () => engineRef.current,
      }}
    >
      {children}
    </EngineContext.Provider>
  );
}

export function useEngineContext(): EngineContextValue {
  const value = useContext(EngineContext);
  if (value === null) {
    throw new Error("useEngineContext must be used inside <EngineProvider>");
  }
  return value;
}
